// packages/shared/src/services/timeTracking/timesheetAggregate.ts
export interface AggInput {
  dossier_id: string;
  started_at: string;        // ISO
  duration_minutes: number;
  is_billable: boolean;
}

export interface Cell { total: number; billable: number; nonBillable: number }
export interface Row { day: string; cells: Record<string, Cell>; dayTotal: number }

export interface TimesheetGrid {
  dossierIds: string[];
  rows: Row[];
  dossierTotals: Record<string, number>;
  grandTotal: number;
}

function dayKey(iso: string): string {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Folds time entries into a day×dossier grid. `dossierIds` is the caller-supplied
 * column list (drives rendering columns); it does NOT filter — entries outside it
 * are still aggregated into cells/totals.
 */
export function aggregateTimesheet(entries: AggInput[], dossierIds: string[]): TimesheetGrid {
  const byDay = new Map<string, Row>();
  const dossierTotals: Record<string, number> = {};
  let grandTotal = 0;

  for (const e of entries) {
    const day = dayKey(e.started_at);
    let row = byDay.get(day);
    if (!row) {
      row = { day, cells: {}, dayTotal: 0 };
      byDay.set(day, row);
    }
    const cell = row.cells[e.dossier_id] ?? { total: 0, billable: 0, nonBillable: 0 };
    cell.total += e.duration_minutes;
    if (e.is_billable) cell.billable += e.duration_minutes;
    else cell.nonBillable += e.duration_minutes;
    row.cells[e.dossier_id] = cell;
    row.dayTotal += e.duration_minutes;
    dossierTotals[e.dossier_id] = (dossierTotals[e.dossier_id] ?? 0) + e.duration_minutes;
    grandTotal += e.duration_minutes;
  }

  const rows = [...byDay.values()].sort((a, b) => a.day.localeCompare(b.day));
  return { dossierIds, rows, dossierTotals, grandTotal };
}
